import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MapPin, Link as LinkIcon, DollarSign, Target, X, UploadCloud, Loader2, Layers, Phone } from 'lucide-react';
import { useCreateFranchiseRequest, useUpdateFranchiseRequest, useFranchiseRequest, useFranchises } from '@/hooks/useFranchise';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/misc';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { NumberInput } from '@/components/ui/numberInput';
import LocationPicker from '@/components/rental/LocationPicker';
import { useUIStore } from '@/store/uiStore';
import { FileUploadDropzone } from '@/components/common';
import BusinessSelector from '@/components/common/BusinessSelector';
import { imageApi } from '@/api/imageApi';
import { useProvinces, useDistricts, useWards } from '@/hooks/useAddress';

const AREA_UNITS = [
  { value: 'm²', label: 'm²' },
  { value: 'ft²', label: 'ft²' },
];
const CURRENCY = [
  { value: 'VND', label: 'VND' },
  { value: 'USD', label: 'USD' },
];



const optionalNumber = z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/,/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  },
  z.number().optional()
);

const schema = z.object({
  franchiseId: z.string().optional().nullable(),
  name: z.string().min(1, 'Vui lòng nhập tên nhu cầu'),
  businessSectorId: z.string().min(1, 'Chọn ngành kinh doanh'),
  businessSectorName: z.string().optional(),
  businessTypeId: z.string().min(1, 'Chọn loại hình kinh doanh'),
  businessTypeName: z.string().optional(),
  preferredLocation: z.string({ required_error: 'Vui lòng nhập địa chỉ cụ thể' }).min(1, 'Vui lòng nhập địa chỉ cụ thể'),
  countryId: z.coerce.number().default(1),
  provinceId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn Tỉnh/Thành phố' }).min(1, 'Vui lòng chọn Tỉnh/Thành phố'),
  districtId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn Quận/Huyện' }).min(1, 'Vui lòng chọn Quận/Huyện'),
  wardId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn Phường/Xã' }).min(1, 'Vui lòng chọn Phường/Xã'),
  currencyUnit: z.string().default('VND'),
  areaUnit: z.string().default('m²'),
  budgetMin: z.preprocess((val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/,/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  }, z.number({ required_error: 'Nhập ngân sách tối thiểu' }).min(1, 'Ngân sách tối thiểu phải > 0')),
  budgetMax: z.preprocess((val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/,/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  }, z.number({ required_error: 'Nhập ngân sách tối đa' }).min(1, 'Ngân sách tối đa phải > 0')),
  preferredAreaSize: optionalNumber,
  expectedStartDate: z.string().optional().nullable(),
  description: z.string().optional(),
  businessExperience: z.string().optional(),
  contactPhone: z.string().min(1, 'Vui lòng nhập số điện thoại'),
  contactEmail: z.string().email('Email không hợp lệ').min(1, 'Vui lòng nhập email'),
});
type FormData = z.infer<typeof schema>;

interface Props {
  FranchiseId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface UploadedImg {
  id: string;
  url: string;
  isPrimary: boolean;
  file?: File;
}

export default function CreateFranchiseRequestPage({ FranchiseId: propFranchiseId, onSuccess, onCancel }: Props = {}) {
  const params = useParams();
  const id = propFranchiseId || params.id;
  const isEditMode = !!id;

  const navigate = useNavigate();
  const { mutate: create, isPending: isCreating } = useCreateFranchiseRequest();
  const { mutate: update, isPending: isUpdating } = useUpdateFranchiseRequest();
  const isPending = isCreating || isUpdating;
  const { addToast } = useUIStore();
  const { data: rawData } = useFranchiseRequest(id || '');
  const existingData = (rawData as any)?.data ?? rawData;
  const { data: franchisesRes } = useFranchises({ limit: 100 });
  const franchisesList = franchisesRes?.data || [];
  const franchiseOptions = franchisesList.map((f: any) => ({
    value: f.id,
    label: f.name || `Thương hiệu #${f.id}`,
  }));

  const [images, setImages] = useState<UploadedImg[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedGeo, setSelectedGeo] = useState<{ lat: string, lon: string, display_name: string } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      countryId: 1, currencyUnit: 'VND', areaUnit: 'm²',
      businessSectorId: '', businessTypeId: '', franchiseId: null
    },
  });

  useEffect(() => {
    if (isEditMode && existingData) {
      reset({
        franchiseId: existingData.franchiseId || existingData.franchise?.id || null,
        name: existingData.name || '',
        businessSectorId: existingData.businessSectorId || '',
        businessSectorName: existingData.businessSectorName || '',
        businessTypeId: existingData.businessTypeId || '',
        businessTypeName: existingData.businessTypeName || '',
        preferredLocation: existingData.preferredLocation || '',
        countryId: existingData.countryId || 1,
        provinceId: existingData.provinceId || 1,
        districtId: existingData.districtId || 1,
        wardId: existingData.wardId || 1,
        budgetMin: existingData.budgetMin,
        budgetMax: existingData.budgetMax,
        currencyUnit: existingData.currencyUnit || 'VND',
        preferredAreaSize: existingData.preferredAreaSize,
        areaUnit: existingData.areaUnit || 'm²',
        expectedStartDate: existingData.expectedStartDate ? existingData.expectedStartDate.split('T')[0] : '',
        description: existingData.description || '',
        businessExperience: existingData.businessExperience || '',
        contactPhone: existingData.contactPhone || '',
        contactEmail: existingData.contactEmail || '',
      } as any);

      if (existingData.latitude && existingData.longitude) {
        setSelectedGeo({
          lat: existingData.latitude.toString(),
          lon: existingData.longitude.toString(),
          display_name: existingData.preferredLocation || '',
        });
      }

      if (existingData.images?.length) {
        setImages(
          existingData.images.map((img: any) => ({
            id: img.imageId || img.id,
            url: img.image?.publicUrl || '',
            isPrimary: img.isPrimary,
          }))
        );
      }
    }
  }, [isEditMode, existingData, reset]);

  const countryId = watch('countryId') || 1;
  const provinceId = watch('provinceId');
  const districtId = watch('districtId');
  const { data: provinces } = useProvinces(countryId);
  const { data: districts } = useDistricts(provinceId);
  const { data: wards } = useWards(districtId);

  const onSubmit = async (data: FormData) => {
    if (data.budgetMax !== undefined && data.budgetMin !== undefined && data.budgetMax < data.budgetMin) {
      addToast('error', 'Ngân sách tối đa phải lớn hơn hoặc bằng ngân sách tối thiểu');
      return;
    }

    try {
      setIsUploading(true);
      const finalImages = [];
      for (const img of images) {
        if (img.file) {
          const res = await imageApi.upload(img.file);
          if (res && res.data) {
            finalImages.push({ id: res.data.id, type: img.isPrimary ? 'banner' : 'detail', isPrimary: img.isPrimary });
          }
        } else {
          finalImages.push({ id: img.id, type: img.isPrimary ? 'banner' : 'detail', isPrimary: img.isPrimary });
        }
      }

      let latitude = selectedGeo?.lat;
      let longitude = selectedGeo?.lon;

      if (!latitude || !longitude) {
        try {
          const provinceName = provinces?.find((p) => p.id === data.provinceId)?.name || '';
          const districtName = districts?.find((d) => d.id === data.districtId)?.name || '';
          const wardName = wards?.find((w) => w.id === data.wardId)?.name || '';

          const fullAddress = [data.preferredLocation, wardName, districtName, provinceName, 'Vietnam']
            .filter(Boolean)
            .join(', ');

          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`, {
            headers: {
              'Accept-Language': 'vi-VN,vi;q=0.9,en-US;q=0.8,en;q=0.7',
            }
          });
          const geoData = await res.json();

          if (geoData && geoData.length > 0) {
            latitude = geoData[0].lat;
            longitude = geoData[0].lon;
          }
        } catch (err) {
          console.error('Failed to geocode address', err);
        }
      }

      const payload = {
        ...data,
        franchiseId: data.franchiseId || undefined,
        businessType: data.businessTypeName,
        businessSectorName: data.businessSectorName,
        budgetMin: Number(data.budgetMin),
        budgetMax: Number(data.budgetMax),
        preferredAreaSize: Number(data.preferredAreaSize || 0),
        images: finalImages,
        expectedStartDate: data.expectedStartDate ? new Date(data.expectedStartDate).toISOString() : null,
        latitude: latitude ? Number(latitude) : undefined,
        longitude: longitude ? Number(longitude) : undefined,
        approvalStatus: 'pending',
      };

      if (isEditMode && id) {
        update({ id, data: payload as any }, {
          onSuccess: () => {
            if (onSuccess) onSuccess();
            else navigate(`/nhu-cau-nhuong-quyen/${id}`);
          }
        });
      } else {
        create(payload as any, {
          onSuccess: (res: any) => {
            if (onSuccess) onSuccess();
            else navigate(`/nhu-cau-nhuong-quyen`);
          }
        });
      }
    } catch (err: any) {
      if (err?.response?.status === 413) {
        addToast('error', 'Ảnh tải lên quá lớn (vượt giới hạn 1MB của máy chủ). Vui lòng chọn ảnh nhỏ hơn.');
      } else {
        addToast('error', 'Lỗi khi tải ảnh lên. Vui lòng thử lại.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const onInvalid = (errors: any) => {
    console.log(errors);
    addToast('error', 'Vui lòng kiểm tra lại các trường thông tin bị thiếu hoặc không hợp lệ.');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newLocalImages: UploadedImg[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        addToast('error', `File ${file.name} không phải là hình ảnh hợp lệ.`);
        continue;
      }

      if (file.size > 1 * 1024 * 1024) {
        addToast('error', `Ảnh ${file.name} vượt quá dung lượng cho phép. Vui lòng chọn ảnh dưới 1MB.`);
        continue;
      }

      newLocalImages.push({
        id: Math.random().toString(36).substring(7),
        url: URL.createObjectURL(file),
        isPrimary: images.length === 0 && i === 0 && newLocalImages.length === 0,
        file,
      });
    }

    setImages((prev) => [...prev, ...newLocalImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const filtered = prev.filter((img) => img.id !== id);
      if (filtered.length > 0 && !filtered.find((img) => img.isPrimary)) {
        filtered[0].isPrimary = true;
      }
      return filtered;
    });
  };

  const setPrimaryImage = (id: string) => {
    setImages((prev) => prev.map((img) => ({ ...img, isPrimary: img.id === id })));
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in pb-10">
      <div className="flex items-center gap-3 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl" style={{ color: 'var(--text-1)' }}>
            {isEditMode ? 'Cập nhật nhu cầu nhượng quyền' : 'Đăng nhu cầu nhượng quyền mặt bằng'}
          </h1>
          <p className="text-base mt-1" style={{ color: 'var(--text-3)' }}>
            Điền đầy đủ thông tin để tìm được mặt bằng phù hợp nhất
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        {/* ── Thông tin chung ── */}
        <Card title="Thông tin chung" icon={<Target size={18} />}>
          <Controller
            name="franchiseId"
            control={control}
            render={({ field }) => (
              <Select
                label="Thương hiệu nhượng quyền"
                options={franchiseOptions}
                placeholder="-- Chọn thương hiệu --"
                value={field.value || ''}
                onChange={(e) => {
                  const selectedId = e.target.value || null;
                  field.onChange(selectedId);
                  if (selectedId) {
                    const selected = franchisesList.find((f: any) => f.id === selectedId);
                    if (selected?.name) {
                      setValue('name', `Yêu cầu hợp tác thương hiệu với ${selected.name}`);
                    }
                  }
                }}
                error={errors.franchiseId?.message}
              />
            )}
          />
          <Input
            label="Tên nhu cầu *"
            placeholder="VD: Yêu cầu hợp tác nhượng quyền Mixue"
            {...register('name')}
            error={errors.name?.message}
          />
          <BusinessSelector
            required
            sectorId={watch('businessSectorId')}
            typeId={watch('businessTypeId')}
            onSectorChange={(id, name) => {
              setValue('businessSectorId', id);
              setValue('businessSectorName', name);
              setValue('businessTypeId', '');
              setValue('businessTypeName', '');
            }}
            onTypeChange={(id, name) => {
              setValue('businessTypeId', id);
              setValue('businessTypeName', name);
            }}
            errors={{
              sectorId: errors.businessSectorId?.message,
              typeId: errors.businessTypeId?.message,
            }}
          />
        </Card>

        {/* ── Thông tin liên hệ ── */}
        <Card title="Thông tin liên hệ" icon={<Phone size={18} />}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input
              label="Số điện thoại *"
              placeholder="VD: 0901234567"
              {...register('contactPhone')}
              error={errors.contactPhone?.message}
            />
            <Input
              label="Email *"
              placeholder="VD: seeker@gmail.com"
              type="email"
              {...register('contactEmail')}
              error={errors.contactEmail?.message}
            />
          </div>
        </Card>

        {/* ── Ngân sách & Yêu cầu không gian ── */}
        <Card title="Ngân sách & Yêu cầu không gian" icon={<DollarSign size={18} />}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Controller
              name="budgetMin"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Ngân sách tối thiểu (VNĐ) *"
                  placeholder="VD: 500,000,000"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.budgetMin?.message}
                />
              )}
            />
            <Controller
              name="budgetMax"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Ngân sách tối đa (VNĐ) *"
                  placeholder="VD: 1,000,000,000"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.budgetMax?.message}
                />
              )}
            />
            <Controller
              name="preferredAreaSize"
              control={control}
              render={({ field }) => (
                <NumberInput
                  label="Diện tích cần thiết (m²)"
                  placeholder="VD: 60"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.preferredAreaSize?.message}
                />
              )}
            />
            <Input
              type="date"
              label="Ngày dự kiến bắt đầu"
              {...register('expectedStartDate')}
              error={errors.expectedStartDate?.message}
            />
          </div>
        </Card>

        {/* ── Khu vực mong muốn ── */}
        <Card title="Vị trí mong muốn" icon={<MapPin size={18} />}>
          <LocationPicker
            addressField="preferredLocation"
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            selectedGeo={selectedGeo}
            onSelectGeo={setSelectedGeo}
          />
        </Card>

        {/* ── Yêu cầu thêm & Kinh nghiệm ── */}
        <Card title="Kinh nghiệm & Ghi chú thêm" icon={<Layers size={18} />}>
          <div className="grid gap-4">
            <Textarea
              label="Kinh nghiệm quản lý/vận hành"
              rows={3}
              placeholder="VD: Đã có kinh nghiệm quản lý vận hành 3 năm trong ngành F&B."
              {...register('businessExperience')}
              error={errors.businessExperience?.message}
            />
            <Textarea
              label="Mô tả chi tiết"
              rows={4}
              placeholder="VD: Tôi đang tìm kiếm cơ hội hợp tác làm đại lý nhượng quyền tại Hải Phòng."
              {...register('description')}
              error={errors.description?.message}
            />
          </div>
        </Card>

        {/* ── Hình ảnh minh hoạ ── */}
        <Card title="Hình ảnh mô phỏng / Concept (Tuỳ chọn)" icon={<LinkIcon size={18} />}>
          <div className="space-y-4">
            <FileUploadDropzone
              onFileSelect={(files) => handleFileUpload({ target: { files } } as any)}
              isUploading={isUploading}
              multiple
              accept="image/*,.pdf"
              title="Nhấn để chọn hoặc kéo thả ảnh concept vào đây"
              subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
            />

            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                {images.map((img) => (
                  <div key={img.id} className={cn(
                    "relative aspect-video rounded-xl overflow-hidden border-2 group",
                    img.isPrimary ? "border-orange-500 shadow-md" : "border-gray-200"
                  )}>
                    <img src={img.url} alt="" className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => removeImage(img.id)}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                        title="Xóa ảnh"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {img.isPrimary ? (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500 text-white text-[10px] font-bold rounded shadow-sm">
                        Ảnh chính
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPrimaryImage(img.id)}
                        className="absolute top-2 left-2 px-2 py-1 bg-black/50 hover:bg-black/80 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Đặt làm ảnh chính
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex gap-4 justify-end pt-4 border-t" style={{ borderColor: 'var(--border-c)' }}>
          <Button type="button" variant="ghost" onClick={() => onCancel ? onCancel() : navigate('/nhu-cau-nhuong-quyen')} className="px-8 font-bold">
            Hủy
          </Button>
          <Button type="submit" variant="brand" loading={isPending || isUploading} className="px-8 font-bold text-base shadow-lg shadow-orange-500/20">
            {isEditMode ? 'Lưu thay đổi' : 'Đăng nhu cầu'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-3xl border p-6 md:p-8 space-y-4 bg-white shadow-sm" style={{ borderColor: 'var(--border-c)' }}>
      <h2 className="font-display font-bold text-lg flex items-center gap-2 pb-4 border-b" style={{ color: 'var(--text-1)', borderColor: 'var(--border-c)' }}>
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500">
          {icon}
        </div>
        {title}
      </h2>
      <div className="pt-2 space-y-5">
        {children}
      </div>
    </div>
  );
}
